/**
 * Qué pasó con el dinero de una confirmación (BB-1643).
 *
 * Vive dentro del desplegable de cada fila del registro. El registro dice qué se confirmó, con qué
 * filtro y cuándo; esto dice si se pagó — que es la primera pregunta de Ops y la que la consola no
 * respondía.
 *
 * Las cifras vienen de `dailyReconciliation` (BB-1642), que cruza el ledger de viajes enviados con
 * las transferencias reales una vez al día. Aquí no se agrega nada: se ordena, se etiqueta y se
 * formatea.
 */
import { Alert, Badge, Group, Stack, Table, Text } from '@mantine/core';

import {
  describirMotivo,
  describirSubestado,
  descuadraElCruce,
  etiquetaEstado,
  pideAtencion,
  viajesEnBuckets,
  type BucketEstado,
  type EstadoConfirmacion,
  type Tono,
} from '../confirmStatus';
import { formatearEur, formatearNumero } from '../formato';
import { describirFrescura } from '../frescura';

export function EstadoTransferencias({
  estado,
  esMock,
  ahoraMs = Date.now(),
}: {
  /** `undefined` = el cruce diario todavía no ha escrito nada para esta confirmación. */
  estado: EstadoConfirmacion | undefined;
  /** Una confirmación en `mock` no movió dinero, así que nunca tendrá cruce. */
  esMock: boolean;
  /** Inyectable para test; en la app es el reloj. */
  ahoraMs?: number;
}) {
  if (estado === undefined) {
    return (
      <Text size="sm" c="dimmed" mt="sm">
        {/* El caso mock no repite la frase del badge de la cabecera ("no se movió dinero"): decirlo
            dos veces en la misma fila no añade nada, y duplicar la frase hacía ambiguo el test que
            protege justo ese aviso. Aquí se dice la consecuencia: no hay nada que cruzar. */}
        {esMock
          ? 'Fue un ensayo en modo mock: no hay pagos reales que cruzar.'
          : 'Todavía sin estado de transferencias. El cruce con los pagos reales corre una vez al día, así que una confirmación reciente aparece aquí al día siguiente.'}
      </Text>
    );
  }

  // El mismo aviso de frescura que el resumen del preview, no una convención nueva: esto también es
  // una foto con fecha, y una foto de hace tres días no dice lo que parece decir.
  const frescura = describirFrescura(estado.calculadoEnIso, ahoraMs);

  return (
    <Stack gap="xs" mt="sm">
      <Group justify="space-between" align="baseline" wrap="wrap" gap="xs">
        <Text size="sm" fw={600}>
          Qué pasó con el dinero
        </Text>
        <Text size="xs" c={frescura.esAviso ? 'orange' : 'dimmed'}>
          {frescura.texto.replace('Datos actualizados', 'Cruce')}
        </Text>
      </Group>

      {frescura.esAviso && (
        <Alert color="orange" title="El cruce no es de hoy">
          {frescura.queHacer ??
            'Estas cifras pueden haber cambiado: el cruce con los pagos reales se recalcula cada día.'}
        </Alert>
      )}

      {estado.buckets.length === 0 ? (
        <Text size="sm" c="dimmed">
          El cruce corrió pero no encontró ningún viaje de esta confirmación en el ledger.
        </Text>
      ) : (
        // Tabla y no barras: aquí se comparan importes entre estados y se leen cifras exactas, no
        // proporciones. La distribución con barras ya existe en el resumen del preview, que es otra
        // pregunta.
        <Table withRowBorders={false} verticalSpacing={4} fz="sm">
          <Table.Tbody>
            {estado.buckets.map((b) => (
              <FilasDeBucket key={b.estado} bucket={b} />
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Text size="xs" c="dimmed">
        {formatearNumero(estado.nViajesEnviados)} viajes enviados en esta confirmación
      </Text>

      {/* La red de seguridad: si el cruce no cuadra con el ledger, se dice. Un total plausible que
          esconde viajes perdidos es peor que un aviso. */}
      {descuadraElCruce(estado) && (
        <Alert color="red" title="El cruce no cuadra con lo enviado">
          Se enviaron {formatearNumero(estado.nViajesEnviados)} viajes y el cruce solo clasifica{' '}
          {formatearNumero(viajesEnBuckets(estado))}. Faltan viajes por explicar: no te fíes de este
          desglose hasta saber por qué.
        </Alert>
      )}
    </Stack>
  );
}

/**
 * Un bucket y, debajo, su desglose (BB-2625): por qué lo rechazó Stripe (`motivos`) o en qué punto está
 * un bloqueado (`subestados`). Cada parte lleva su tono y su definición, porque "Bloqueado por
 * certificación" a secas mezclaba lo que aún no se había enviado con lo que hay que llevar a Backend.
 */
function FilasDeBucket({ bucket: b }: { bucket: BucketEstado }) {
  const partes = [
    ...(b.subestados ?? []).map((s) => ({
      clave: s.subestado,
      ...describirSubestado(s.subestado),
      nViajes: s.nViajes,
      nUsuarios: null as number | null,
      importeEur: s.importeEur,
      transitorio: false,
    })),
    ...(b.motivos ?? []).map((m) => ({
      clave: `${m.motivo}|${m.providerResultCode ?? ''}`,
      ...describirMotivo(m),
      nViajes: m.nViajes,
      nUsuarios: m.nUsuarios as number | null,
      importeEur: m.importeEur,
    })),
  ];

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Group gap={6} wrap="nowrap">
            {/* Palabra y color, nunca color solo: el aviso tiene que llegar a quien no distingue el
                rojo. */}
            <Text size="sm" fw={partes.length > 0 ? 600 : undefined}>
              {etiquetaEstado(b.estado, b)}
            </Text>
            {/* Con desglose, el chip va en la parte que lo pide y no se repite aqui. */}
            {partes.length === 0 && pideAtencion(b) && <ChipRevisar />}
          </Group>
        </Table.Td>
        <Table.Td ta="right">
          <Text size="sm" c="dimmed">
            {formatearNumero(b.nViajes)} viajes
          </Text>
        </Table.Td>
        <Table.Td ta="right" w={110}>
          <Importe importeEur={b.importeEur} />
        </Table.Td>
      </Table.Tr>
      {partes.map((p) => (
        <Table.Tr key={p.clave}>
          <Table.Td pl="lg">
            <Group gap={6} wrap="nowrap">
              <Text size="xs" c={COLOR_TONO[p.tono]}>
                {p.etiqueta}
              </Text>
              {p.tono === 'alarma' && <ChipRevisar />}
              {p.transitorio && (
                <Badge color="gray" variant="light" size="xs">
                  se resuelve solo
                </Badge>
              )}
            </Group>
            {p.definicion && (
              <Text size="xs" c="dimmed" maw={420}>
                {p.definicion}
              </Text>
            )}
          </Table.Td>
          <Table.Td ta="right" valign="top">
            <Text size="xs" c="dimmed">
              {formatearNumero(p.nViajes)} viajes
              {p.nUsuarios !== null &&
                ` · ${formatearNumero(p.nUsuarios)} ${p.nUsuarios === 1 ? 'conductor' : 'conductores'}`}
            </Text>
          </Table.Td>
          <Table.Td ta="right" valign="top">
            <Text size="xs">{formatearEur(p.importeEur)}</Text>
          </Table.Td>
        </Table.Tr>
      ))}
    </>
  );
}

const COLOR_TONO: Record<Tono, string | undefined> = {
  alarma: 'red',
  aviso: 'orange',
  neutro: undefined,
};

/** El único chip rojo de la tabla: palabra además de color. */
function ChipRevisar() {
  return (
    <Badge color="red" variant="light" size="sm">
      revisar
    </Badge>
  );
}

/** Sin importe no es cero euros: es que no hay transferencia de la que sacarlo. */
function Importe({ importeEur }: { importeEur: string | null }) {
  return importeEur === null ? (
    <Text size="sm" c="dimmed">
      —
    </Text>
  ) : (
    <Text size="sm" fw={500}>
      {formatearEur(importeEur)}
    </Text>
  );
}
